import { AxiosError } from 'axios';
import { observer } from 'mobx-react';
import React, { useCallback, useEffect, useLayoutEffect, useRef } from 'react';

import EntityDetails from 'stores/EntityDetails/EntityDetails';

import { useStore } from 'utils/hooks/store';

interface EntitiesDetailsLoaderChildrenProps {
  isLoaded: boolean;
  error?: AxiosError;
  reload: () => void;
}

interface EntitiesDetailsLoaderProps<Params> {
  params: Readonly<{ [K in keyof Params]?: string | undefined }>;
  store: EntityDetails;
  routeParam: keyof Params;
  children: (props: EntitiesDetailsLoaderChildrenProps) => React.ReactElement;
  avoidReset?: boolean;
}

function EntitiesDetailsLoader<Params>({
  store,
  routeParam,
  params,
  children,
  avoidReset = false,
}: EntitiesDetailsLoaderProps<Params>): React.ReactElement {
  const { entityFields } = useStore();

  const prevParamRef = useRef<string | undefined>();
  const hasInitializedRef = useRef(false);

  // The record id, not the `params` object: useParams returns a fresh object every render, so the
  // mount effect below re-ran and refetched the whole record on every one.
  const recordId = params[routeParam] as string | undefined;

  const loadDetails = useCallback(async (): Promise<void> => {
    await entityFields.loadFields(store.entityType);
    await store.loadDetails(recordId as string);
  }, [store, recordId, entityFields]);

  // Reset store synchronously on initial mount to prevent showing wrong state
  // This runs before the first paint, preventing the NotFound flash
  useLayoutEffect(() => {
    if (!hasInitializedRef.current) {
      hasInitializedRef.current = true;
      // Reset store to ensure clean state before loading
      // This prevents showing NotFound when navigating back to the page
      store.reset();
    }
  }, [store]);

  // Handle initial mount
  useEffect(() => {
    loadDetails();
  }, [loadDetails]);

  // Handle param changes
  useEffect(() => {
    const prevParam = prevParamRef.current;

    if (prevParam !== undefined && recordId !== prevParam) {
      store.reset();
      loadDetails();
    }

    prevParamRef.current = recordId;
  }, [recordId, store, loadDetails]);

  // Handle unmount
  useEffect(
    () => () => {
      if (!avoidReset) {
        store.reset();
      }
    },
    [avoidReset, store]
  );

  const isLoaded = store.isLoaded && entityFields.isLoaded;
  const error = store.error || entityFields.error;

  return children({
    isLoaded: isLoaded && (store.details !== undefined || Boolean(error)),
    error,
    reload: loadDetails,
  });
}

export default observer(EntitiesDetailsLoader);
